import {
  SgcfRetailer,
  SgRetailersSearchResponse,
  SgcfStore,
} from '@scentregroup/shared/graphql'
import { isDefinedAndNotNull } from '@scentregroup/shared/lib'
import {
  ViewType,
  isViewType,
} from '@scentregroup/shared/hub-components/view-control'
import { ParsedUrlQuery } from 'querystring'
import { ContentPageData } from '../../types/content-page'

export type StoresBreadcrumbPath = {
  title: string
  slug: string
}[]

export type PopularCategory = {
  id: string
  title?: string
  slug?: string
  imageUrl?: string | null
}

export type RetailCategory = {
  id: string
  title: string
  shortDescription?: string | null
  parentId?: string
  slug: string
  level: number
  children?: RetailCategory[]
}

export interface IStoreDirectory {
  total: number
  items: IStoreRetailerItem[]
}

interface StoreRetailerBase {
  id: string
  spaceId: string
  title: string
  slug: string
  storefrontUrl: string
  imageUrl?: string
  retailCategories: string[]
}

type Retailer = StoreRetailerBase & {
  _type: 'RETAILER'
}
type Store = StoreRetailerBase & {
  _type: 'STORE'
  centreSlug: string
}

export type IStoreRetailerItem = Retailer | Store

type ReatailerCategoriesItem = {
  sys: {
    id: string
  }
  title: string | null
  level: number | null
  parentCategory: {
    sys: { id: string }
  } | null
}

type StoreItem = Partial<Omit<SgcfStore, 'sys' | 'centre'>> & {
  sys: { id: string }
  centre: Centre | null
}

type Centre = {
  slug: string | null
}

type SGCfRetailerPicked =
  | (Partial<
      Omit<
        SgcfRetailer,
        | 'sys'
        | 'retailerLogoSet'
        | 'retailCategories'
        | 'storesFromRetailersSearch'
        | 'nationalStorefront'
      >
    > & {
      sys: { id: string; spaceId: string }
      retailerLogoSet?: {
        coloured: {
          url: string | null
        } | null
      } | null
      retailCategories?: (ReatailerCategoriesItem | null)[] | null
      storesFromRetailersSearch?: {
        total: number | null
        items: (StoreItem | null)[] | null
      } | null
      nationalStorefront: { sys: { id: string } } | null
    })
  | null

const e = (uriComponent?: string | null): string =>
  encodeURIComponent(uriComponent ?? '')

export function makePopularCategoriesFromStoresContent(
  content: ContentPageData
): PopularCategory[] | null {
  const jsonData = content.jsonData as Record<
    'popularCategories',
    PopularCategory[]
  > | null
  if (!jsonData?.popularCategories) {
    return null
  }
  return jsonData.popularCategories
}

export function makeNationalStoreDirectoryFromSgRetailersSearchResponse(
  sgRetailersSearch: Pick<SgRetailersSearchResponse, 'total'> & {
    items: SGCfRetailerPicked[]
  }
): IStoreDirectory {
  return {
    total: sgRetailersSearch.total,
    items: sgRetailersSearch.items
      .filter(isDefinedAndNotNull)
      .map(makeNationalStoreDirectoryItemFromSGCfRetailer),
  }
}

export function makeNationalStoreDirectoryItemFromSGCfRetailer(
  sgcfRetailer: SGCfRetailerPicked
): IStoreRetailerItem {
  if (sgcfRetailer === null) {
    throw new Error('Unexpected missing retailer')
  }
  const hasNationalStorefront = Boolean(sgcfRetailer.nationalStorefront)
  const isSingleStore = sgcfRetailer.storesFromRetailersSearch?.total == 1
  const spaceId = sgcfRetailer.sys.spaceId

  const retailCategories = makeRetailerCategoriesFromCollection(
    sgcfRetailer.retailCategories
  )
  const store = sgcfRetailer.storesFromRetailersSearch?.items?.[0]
  const imageUrl = sgcfRetailer.retailerLogoSet?.coloured?.url
  if (hasNationalStorefront || !isSingleStore || !store) {
    const id = sgcfRetailer.sys.id
    const { slug, title } = sgcfRetailer
    const storefrontUrl = `/store/${e(id)}/${e(slug)}`
    return {
      _type: 'RETAILER',
      id,
      spaceId,
      title: title || '',
      slug: slug || '',
      retailCategories,
      storefrontUrl,
      ...(imageUrl ? { imageUrl } : undefined),
    }
  } else {
    const { slug } = store
    const { title } = sgcfRetailer
    const centreSlug = store.centre?.slug
    const id = store?.sys.id
    const storefrontUrl = `/${e(centreSlug)}/store/${e(id)}/${e(slug)}`

    return {
      _type: 'STORE',
      id,
      spaceId,
      title: title || '',
      slug: slug || '',
      centreSlug: centreSlug || '',
      retailCategories,
      storefrontUrl,
      ...(imageUrl ? { imageUrl } : undefined),
    }
  }
}

function makeRetailerCategoriesFromCollection(
  cfRetailerCategories: (ReatailerCategoriesItem | null)[] | null | undefined
): string[] {
  return (cfRetailerCategories || [])
    .filter(isDefinedAndNotNull)
    .map(item => item.title)
    .filter(isDefinedAndNotNull)
    .slice(0, 3)
}

export type FilterFormData = {
  viewMode: ViewType
  sortBy: 'asc' | 'desc'
}

export const isSortBy = (value: unknown): value is FilterFormData['sortBy'] => {
  return typeof value === 'string' && ['asc', 'desc'].includes(value)
}

export const getFormdataFromQuery = (
  query: ParsedUrlQuery
): FilterFormData => ({
  viewMode: isViewType(query.viewMode) ? query.viewMode : 'auto',
  sortBy: isSortBy(query.sortBy) ? query.sortBy : 'asc',
})

export function isINationalStoreDirectoryRetailerItem(
  item: IStoreRetailerItem
): item is Retailer {
  return item._type === 'RETAILER'
}

export function isIStoreDirectoryStoreItem(
  item: IStoreRetailerItem
): item is Store {
  return item._type === 'STORE'
}
